import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { Card, Typography, Chip, Grid2, TextField, Autocomplete, Box, Tooltip } from "@mui/material";
// import { MoreVert } from "@mui/icons-material";
import { usePageWrapper } from "../../../../hooks";
import { ArtTable, ArtTableHandle, FaIcon, HeadCell, SSRTableBuilder, useApiService, ArtLineChart, ArtStatusView } from "@art/shared-ui";
import { useParams } from "react-router-dom";
import { faArrowTurnDown, faArrowTurnUp, faUserCheck, faUsers, faUserShield, faCircleNodes } from "@fortawesome/free-solid-svg-icons";
// import TestMessage from "../test-message";
// import ConnectionMessage from "../connection-message";
const ArtProfileInsight = usePageWrapper(function () {
  interface UserData {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    connection_count: Count;
    channel_count: Count;
    group_count: number;
    total_count: number;
    messages_sent_count: number;
    messages_received_count: number;
    roles: string[];
  }

  interface Count {
    total_count: number;
    active_count: number;
    inActive_count: number;
  }

  interface Connection {
    id: string
    ID: string;
    ConnectionType: string;
    Platform: string;
    Status: string;
    TimeInfo: string;
  }

  const [userData, setUserData] = useState<UserData | null>(null);
  const apiService = useApiService();
  const { id } = useParams();
  const tableRef = useRef<ArtTableHandle>(null);
  // const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // const [openDialog, setOpenDialog] = useState(false);
  // const [selectedData, setSelectedData]: [Data | null, any] = useState(null);
  // const [openTableDialog, setOpenTableDialog] = useState(false);

  const [timelineData, setTimelineData] = useState<{
    xAxis: string[];
    series: number[];
  } | null>(null);

  const [dateFilter, setDateFilter] = useState<'7' | '15' | '30'>('7'); // Default 7 days
  const handleFilterChange = (newFilter: '7' | '15' | '30') => {
    setDateFilter(newFilter);
    fetchTimelineData(newFilter);
  };


  const getDateRangeFromDays = (days: number) => {
    const end = new Date();
    const start = new Date();

    start.setDate(end.getDate() - days + 1);

    const format = (date: Date) =>
      date.toISOString().split('T')[0]; // YYYY-MM-DD

    return {
      start_date: format(start),
      end_date: format(end),
    };
  };

  const fetchTimelineData = async (filterValue?: '7' | '15' | '30') => {
    try {
      const days = Number(filterValue ?? dateFilter);
      const { start_date, end_date } = getDateRangeFromDays(days);

      const response = await apiService.get(
        `connections/user/${id}/status-timeline`,
        {
          params: { start_date, end_date },
        }
      );

      setTimelineData(response.data.data);
    } catch (error) {
      console.error("Failed to fetch timeline:", error);
      setTimelineData(null);
    }
  };


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await apiService.get(`art-users/${id}/profile`);
        setUserData(response?.data.data);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };


    fetchUserData();

    fetchTimelineData(dateFilter);

  }, []);

  const userDetails = userData
    ? [
      { label: "ID", value: userData.id },
      { label: "Username", value: userData.username },
      { label: "First Name", value: userData.first_name },
      { label: "Last Name", value: userData.last_name },
      { label: "Total no. connections", value: userData.connection_count.total_count },
    ]
    : [];

  const overviewData = userData
    ? [
      { label: "Subscribed Channels", value: userData?.channel_count.total_count, icon: <FaIcon icon={faUserCheck} /> },
      { label: "Groups", value: userData?.group_count, icon: <FaIcon icon={faUsers} /> },
      { label: "Messages Sent", value: userData?.messages_sent_count, icon: <FaIcon icon={faArrowTurnUp} /> },
      { label: "Messages Received", value: userData?.messages_received_count, icon: <FaIcon icon={faArrowTurnDown} /> },
      { label: "Active Connections", value: userData?.connection_count.active_count, icon: <FaIcon icon={faCircleNodes} /> },
      { label: "Roles Assigned", value: userData?.roles?.length ? userData.roles.map((x: any) => x.title).join(", ") : "No assigned roles", icon: <FaIcon icon={faUserShield} /> },

    ]
    : [];

  const headCells: HeadCell<Connection>[] = [
    {
      id: "ConnectionType",
      numeric: false,
      disablePadding: false,
      label: "Connection Type",
      getColData: (row) => (
        <Box>
          <Chip
            label={row.ConnectionType}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          {row.ID && (
            <Tooltip title="Click to copy full ID" arrow placement="top">
              <Typography
                variant="caption"
                color="text.secondary"
                onClick={() => {
                  navigator.clipboard.writeText(row.ID);
                  apiService.showSnack("Connection ID copied", "success");
                }}
                sx={{
                  display: "block",
                  mt: 0.5,
                  maxWidth: 160,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontFamily: "monospace",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {row.ID}
              </Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      id: "Platform",
      numeric: false,
      disablePadding: false,
      label: "Platform",
      getColData: (row) => row.Platform,
    },
    {
      id: "Status",
      numeric: false,
      disablePadding: false,
      label: "Status",
      getColData: (row) => {
        let chipColor: "default" | "success" | "error" = "default";

        if (row.Status === "active") chipColor = "success";
        else if (row.Status === "inactive") chipColor = "error";

          return <Chip label={row.Status.charAt(0).toUpperCase() + row.Status.slice(1)} color={chipColor} size="small" />;

    },
  },
  {
    id: "TimeInfo",
    numeric: false,
    disablePadding: false,
    label: "Timestamp",
    getColData: (row) => (
      <Tooltip title={row.TimeInfo ? dayjs(row.TimeInfo).format("MMMM D, YYYY h:mm:ss A") : ""} arrow placement="top">
        <span>{row.TimeInfo ? dayjs(row.TimeInfo).format("MMM D, YYYY h:mm A") : "--"}</span>
      </Tooltip>
    ),
  },
];


  const tableBuilder = SSRTableBuilder<Connection>({
    endpoint: `/connections/user/${id}`,
    method: "get",
    apiService: apiService,

    onFetch: (params: any) => {
      return {
        page: params.page,
      };
    },

    onResponse: (res: any) => {
      const rawConnections = res?.data?.connections ?? [];

      const mappedConnections: Connection[] = rawConnections.map((c: any) => {
        const latestSession = c.Sessions?.[0];

        return {
          ID: c.ID,
          ConnectionType: c.ConnectionType || "-",
          Platform: c.Platform || "-",
          Status: latestSession?.Status ?? "inactive",
          TimeInfo: c.TimeInfo,
        };
      });

      return {
        data: mappedConnections,
        total: res?.data?.total_count ?? 0,
      };
    },
  });



  return (
    <div className="px-2 py-2">
      <Typography variant='h3' className='art_dashboard_title mb-2'>Profile Insight</Typography>
      <Typography variant='body2' color="textDisabled" className='mb-3'>The user profile view offers a detailed overview of activity and engagement, including total groups, roles, messages sent and received, and subscribed channels.
        A connection table also displays the status and platform of each connection, providing insights into accessibility and interactions across devices.</Typography>

      <Card className="p-3 mb-3 user-details-card art_dashboard_card">
        <div className="row align-items-center">
          <div className="col-12 col-md-10 col-xl-10 text-break">
            <div className="row">
              {userDetails.map((item, index) => (
                <div key={index} className="col-md-4 mb-3">
                  <Typography variant="body2">
                    <strong>{item.label}:</strong> {item.value}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
          <div className="col-md-2 text-end">
          </div>
        </div>
      </Card>
      <div className="row mb-3">
        <div className="col-12 d-flex justify-content-end">
          <div style={{ width: '200px' }}>
            <Autocomplete
            disableClearable
              options={[
                { label: 'Last 7 Days', value: '7' },
                { label: 'Last 15 Days', value: '15' },
                { label: 'Last 30 Days', value: '30' }
              ]}
              getOptionLabel={(option: { label: string; value: string }) => option.label}
              value={{ label: `Last ${dateFilter} Days`, value: dateFilter }}
              onChange={(_, newValue: { label: string; value: string } | null) => {
                if (newValue) {
                  handleFilterChange(newValue.value as '7' | '15' | '30');
                }
              }}

              renderInput={(params: any) => (
                <TextField {...params} label="Time Range" variant="outlined" />
              )}

              size="small"
            />
          </div>
        </div>
      </div>

      <Grid2 container spacing={2}>
        {/* <Grid2 size={4}> */}
        <Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
          <Card className="p-3 overview-card art_dashboard_card">
            <Typography variant="h3" className="mb-4">Overview</Typography>
            <div className="mt-3">
              {overviewData.map((item, index) => (
                <div key={index} className="d-flex align-items-start overview-item my-3 gap-4">
                  <div className="icon" style={{ color: "#04adc3", height: 20, width: 20 }}>{item.icon}</div>
                  <div>
                    <Typography variant="body2" className="fs-6"><strong>{item.label}</strong></Typography>
                    <Typography variant="body2" color="textDisabled">{item.value}</Typography>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Grid2>
        {/* <Grid2 size={8}> */}
        <Grid2 size={{ xs: 12, sm: 12, md: 8 }}>


          <Card className="p-3 overview-card art_dashboard_card" sx={{ height: '-webkit-fill-available' }} >
            <Typography variant="h3" className="mb-2">
              Connections Over Time
            </Typography>

            <Typography variant="body2" color="textDisabled">
              Highlights daily connection trends to help analyze user engagement over time.
            </Typography>

            {timelineData?.xAxis?.length ? (
              <ArtLineChart
                xAxisData={timelineData.xAxis}
                seriesData={timelineData.series}
                seriesName="Total Connections"
                smooth
              />
            ) : (
              <ArtStatusView
                description="No data available for selected time range"
                imageSrc="/assets/images/no_chart_data.jpeg"
              />
            )}
          </Card>

        </Grid2>
      </Grid2>

      <Typography variant="h3" className="mb-2 mt-4">Connections</Typography>

        <Box className="non-clickable-table">

      <ArtTable ref={tableRef} headCells={headCells} builder={tableBuilder} disableEmptyRows={true} denseRows={true}
      // rowActions={[
      //   {
      //     icon: faEdit,
      //     label: 'Edit',
      //     onClick: (row: Connection) => {
      //       setSelectedData(row);
      //       setOpenTableDialog(true);
      //     }
      //   }
      // ]}
      />
      </Box>

      {/* <ArtDialog
        title="Test"
        open={openTableDialog}
        onClose={handleCloseTableDialog}
        type="form"
        actionBuilder={(props) => {
          return <Box sx={{ display: 'flex', gap: '15px' }}>
            <ArtButton buttonType="button" dataQa="btn-save" label="Test" onClick={() => props.submit?.()} />
            <ArtButton buttonType="button" dataQa="btn-reset" label="Reset" variant="outlined" color="error" onClick={() => props.reset?.()} />
          </Box>
        }}>
        <ConnectionMessage data={selectedData} closeDialog={handleCloseTableDialog} />

      </ArtDialog> */}


    </div>
  );
}, { page_title: 'Profile Insight' });

export { ArtProfileInsight };

